import {
  toBool,
  toFiniteNumber,
  normalizeConfidenceScore,
  enrichPriceEstimateFields,
  formatExportTimestamp,
  getItemExplanation,
} from '../formatters';
import { computeAccuracyRatio, uniqueNonEmptyUrls } from './numberUtils';
import {
  getResultAttemptSourceInfo,
  getResultCacheInfo,
  getResultTokenCounts,
} from './metrics';

export const escapeCsvCell = (value) => {
  let text = '';
  if (value !== null && value !== undefined) {
    if (typeof value === 'object') {
      try {
        text = JSON.stringify(value);
      } catch (_) {
        text = String(value);
      }
    } else {
      text = String(value);
    }
  }
  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

export const toSafeFilenamePart = (value, fallback = '') => {
  const normalized = String(value || '')
    .trim()
    .replace(/[^a-z0-9_-]+/gi, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || fallback;
};

export const buildRunExportRows = (run = {}) => {
  const runResults = Array.isArray(run?.results) ? run.results : [];
  const runTimestamp = formatExportTimestamp(run?.timestamp);
  const rows = [];

  runResults.forEach((result, resultIndex) => {
    const resultItems =
      Array.isArray(result?.items) && result.items.length > 0 ? result.items : [null];
    const resultImageUrls = Array.isArray(result?.image_urls)
      ? result.image_urls
      : typeof result?.image_urls === 'string' && result.image_urls.trim()
        ? [result.image_urls]
        : [];
    const lotNumber = result?.['lot#'] ?? '';
    const cacheInfo = getResultCacheInfo(result);
    const attemptSourceInfo = getResultAttemptSourceInfo(result);
    const durationFromAnalysis = toFiniteNumber(result?.analysis_duration_ms);
    const durationSeconds = toFiniteNumber(result?.duration);
    const durationMs =
      durationFromAnalysis !== null
        ? Number(durationFromAnalysis.toFixed(0))
        : durationSeconds === null
          ? ''
          : Number((durationSeconds * 1000).toFixed(0));
    const cost = toFiniteNumber(result?.analysis_cost ?? result?.cost);
    const resultTokenTotals = getResultTokenCounts(result);
    const estimatedSum = Array.isArray(result?.items)
      ? result.items.reduce((sum, entry) => sum + (toFiniteNumber(entry?.estimated) || 0), 0)
      : 0;
    const lotActual = Array.isArray(result?.items)
      ? result.items
          .map((entry) => toFiniteNumber(entry?.actual))
          .find((value) => value !== null)
      : null;
    const lotAccuracy = computeAccuracyRatio(lotActual, estimatedSum);

    resultItems.forEach((item, itemIndex) => {
      const itemImageUrls = Array.isArray(item?.image_urls)
        ? item.image_urls
        : typeof item?.image_urls === 'string' && item.image_urls.trim()
          ? [item.image_urls]
          : [];
      const singleItemImageUrl =
        typeof item?.image_url === 'string' && item.image_url.trim() ? [item.image_url] : [];
      const mergedImageUrls = uniqueNonEmptyUrls([
        ...resultImageUrls,
        ...itemImageUrls,
        ...singleItemImageUrl,
      ]);

      const actual = toFiniteNumber(item?.actual);
      const estimated = toFiniteNumber(item?.estimated);
      const confidenceScore = normalizeConfidenceScore(item?.confidence_score ?? item?.confidence);
      const enrichedItem = enrichPriceEstimateFields(item || {});
      const estimatedRange = enrichedItem?.estimated_value_range;
      const estimatedLow = toFiniteNumber(estimatedRange?.low);
      const estimatedHigh = toFiniteNumber(estimatedRange?.high);
      const estimatedDistribution = Array.isArray(enrichedItem?.price_estimate_distribution)
        ? enrichedItem.price_estimate_distribution
        : [];
      const itemAccuracy = computeAccuracyRatio(actual, estimated);
      const explanation = getItemExplanation(item);

      rows.push({
        run_timestamp: runTimestamp,
        run_id: run?.run_id || '',
        dataset_name: run?.dataset_name || '',
        dataset_prefix: run?.dataset_prefix || '',
        model: run?.model || '',
        mode: run?.mode || '',
        resize_mode: run?.resize_mode || '',
        compression_mode: run?.compression_mode || '',
        use_ebay_rag: toBool(run?.use_ebay_rag, false) ? 'true' : 'false',
        use_notes_rag: toBool(run?.use_notes_rag, false) ? 'true' : 'false',
        assume_hidden_items: toBool(run?.assume_hidden_items, true) ? 'true' : 'false',
        lot_number: item?.['lot#'] ?? lotNumber,
        result_index: resultIndex,
        item_index: item ? itemIndex : '',
        item_name: item?.item || item?.item_name || '',
        actual_price: actual === null ? '' : actual,
        estimated_price: estimated === null ? '' : estimated,
        confidence_score: confidenceScore === null ? '' : confidenceScore,
        estimated_range_low: estimatedLow === null ? '' : estimatedLow,
        estimated_range_high: estimatedHigh === null ? '' : estimatedHigh,
        estimated_range_json: estimatedRange || '',
        price_estimate_distribution: estimatedDistribution,
        ai_estimated_sum: estimatedSum,
        item_accuracy_percent: itemAccuracy === null ? '' : Number((itemAccuracy * 100).toFixed(2)),
        lot_accuracy_percent: lotAccuracy === null ? '' : Number((lotAccuracy * 100).toFixed(2)),
        explanation: explanation || '',
        review_note: item?.review_note || '',
        duration_ms: durationMs,
        cost_usd: cost === null ? '' : Number(cost.toFixed(6)),
        first_attempt_source: attemptSourceInfo.firstSource,
        final_attempt_source: attemptSourceInfo.finalSource,
        first_attempt_cache_hit:
          attemptSourceInfo.firstCacheHit === null
            ? ''
            : attemptSourceInfo.firstCacheHit
              ? 'true'
              : 'false',
        final_attempt_cache_hit:
          attemptSourceInfo.finalCacheHit === null
            ? ''
            : attemptSourceInfo.finalCacheHit
              ? 'true'
              : 'false',
        analysis_source: cacheInfo.source,
        cache_hit: cacheInfo.cacheHit ? 'true' : 'false',
        cache_key: cacheInfo.cacheKey || '',
        input_tokens_consumed: resultTokenTotals.input,
        output_tokens_consumed: resultTokenTotals.output,
        total_tokens_consumed: resultTokenTotals.total,
        image_urls: mergedImageUrls.join(' | '),
        bounding_box: item?.bounding_box || '',
        raw_item: item || '',
      });
    });
  });

  return rows;
};

export const CSV_EXPORT_HEADERS = [
  'run_timestamp',
  'run_id',
  'dataset_name',
  'dataset_prefix',
  'model',
  'mode',
  'resize_mode',
  'compression_mode',
  'use_ebay_rag',
  'use_notes_rag',
  'assume_hidden_items',
  'lot_number',
  'result_index',
  'item_index',
  'item_name',
  'actual_price',
  'estimated_price',
  'confidence_score',
  'estimated_range_low',
  'estimated_range_high',
  'estimated_range_json',
  'price_estimate_distribution',
  'ai_estimated_sum',
  'item_accuracy_percent',
  'lot_accuracy_percent',
  'explanation',
  'review_note',
  'duration_ms',
  'cost_usd',
  'first_attempt_source',
  'final_attempt_source',
  'first_attempt_cache_hit',
  'final_attempt_cache_hit',
  'analysis_source',
  'cache_hit',
  'cache_key',
  'input_tokens_consumed',
  'output_tokens_consumed',
  'total_tokens_consumed',
  'image_urls',
  'bounding_box',
  'raw_item',
];

export const exportRunToCsv = (run = {}, displayedIndex = 0) => {
  const rows = buildRunExportRows(run);
  if (rows.length === 0) {
    alert('No detailed row results found for this test run.');
    return;
  }

  const csvLines = [
    CSV_EXPORT_HEADERS.join(','),
    ...rows.map((row) =>
      CSV_EXPORT_HEADERS.map((header) => escapeCsvCell(row[header])).join(','),
    ),
  ];
  const blob = new Blob([csvLines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const objectUrl = URL.createObjectURL(blob);

  const runPart = toSafeFilenamePart(run?.run_id, `run-${displayedIndex + 1}`);
  const timestampPart = toSafeFilenamePart(
    formatExportTimestamp(run?.timestamp).replace(/[:.]/g, '-'),
    'timestamp',
  );
  const fileName = `test-run-${runPart}-${timestampPart}.csv`;

  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};
